import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { motion } from 'motion/react'
import { assessAffordability, getAffordabilityPercent } from '@/lib/mortgage'
import type { AffordabilityLevel } from '@/lib/mortgage'

interface AffordabilityGaugeProps {
  monthlyPayment: number
  monthlyIncome: number
}

const levelConfig: Record<
  AffordabilityLevel,
  { color: string; bgColor: string; barGradient: string; emoji: string }
> = {
  comfortable: {
    color: 'text-teal-700',
    bgColor: 'bg-gradient-to-br from-teal-50 to-emerald-50',
    barGradient: 'from-teal-400 to-emerald-500',
    emoji: '😊',
  },
  moderate: {
    color: 'text-blue-700',
    bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
    barGradient: 'from-blue-400 to-indigo-500',
    emoji: '🤔',
  },
  tight: {
    color: 'text-amber-700',
    bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
    barGradient: 'from-amber-400 to-orange-500',
    emoji: '😰',
  },
  risky: {
    color: 'text-rose-700',
    bgColor: 'bg-gradient-to-br from-rose-50 to-red-50',
    barGradient: 'from-rose-400 to-red-500',
    emoji: '🚫',
  },
}

export function AffordabilityGauge({ monthlyPayment, monthlyIncome }: AffordabilityGaugeProps) {
  const { t } = useTranslation()
  const level = assessAffordability(monthlyPayment, monthlyIncome)
  const percent = getAffordabilityPercent(monthlyPayment, monthlyIncome)
  const config = levelConfig[level]

  return (
    <Card className={`${config.bgColor} border-0 overflow-hidden`}>
      <CardContent className="py-4 px-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">{t('affordability.title')}</h2>
          <motion.div
            key={level}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex items-center gap-1.5"
          >
            <motion.span
              animate={level === 'risky' ? { scale: [1, 1.15, 1] } : {}}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="text-lg"
            >
              {config.emoji}
            </motion.span>
            <span className={`text-sm font-bold ${config.color}`}>
              {t(`affordability.${level}`)}
            </span>
          </motion.div>
        </div>

        <div className="relative h-4 bg-white/60 rounded-full overflow-hidden shadow-inner">
          <motion.div
            className={`h-full rounded-full bg-gradient-to-r ${config.barGradient}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percent, 100)}%` }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
          />
          <div className="absolute top-0 left-[25%] h-full w-px bg-black/10" />
          <div className="absolute top-0 left-[35%] h-full w-px bg-black/15" />
          <div className="absolute top-0 left-[50%] h-full w-px bg-black/20" />
        </div>

        <div className="flex items-start justify-between gap-2">
          <p className={`text-xs ${config.color}`}>
            {t(`affordability.${level}Desc`)}
          </p>
          <span className={`text-sm font-bold ${config.color} whitespace-nowrap`}>
            {t('affordability.incomeRatio', { percent: percent.toFixed(1) })}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
